import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useInviteUser, usePatchUser } from '@/generated/client/users/users';
import { useNotificationStore } from '@/stores/notificationstore';

import { createUserMutationErrorHandler } from './userMutationErrors';
import { userKeys } from './userQueryKeys';

export function useCreateUserMutation(onSuccess: () => Promise<void>) {
  const { t } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const queryClient = useQueryClient();
  const handleMutationError = createUserMutationErrorHandler({
    operation: 'create-user',
    showNotification,
    t,
  });
  return useInviteUser({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: userKeys.lists() });
        showNotification('success', t('userAccessControl.messages.created'));
        await onSuccess();
      },
      onError: handleMutationError,
    },
  });
}

export function useUpdateUserMutation(userId: number, onSuccess: () => Promise<void>) {
  const { t } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const queryClient = useQueryClient();
  const handleMutationError = createUserMutationErrorHandler({
    operation: 'update-user',
    showNotification,
    t,
  });
  return usePatchUser({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) }),
        ]);
        showNotification('success', t('userAccessControl.messages.updated'));
        await onSuccess();
      },
      onError: handleMutationError,
    },
  });
}
